/* eslint-disable @typescript-eslint/ban-ts-comment */
import { IoClose, IoSearchOutline } from "react-icons/io5";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { useTheme } from "../../../context/ThemeProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { updateSearchMessageResult } from "../../../store/Slices/SearchMessageSlice";
import { ClipLoader } from "react-spinners";
import { updateNavigateToSpesificMessage } from "../../../store/Slices/NavigateToSpesificMessageSlice";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { useSearchMessages } from "../../../store/api/useSearchMessages";
import { searchMessageRecord } from "../../../types/ResType";
import ShowSavedName from "../../../components/ShowSavedName";
import chatSidebarTime from "../../../utils/chatSidebarTime";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";

function previewForMessage(message: searchMessageRecord): string {
  const type = message.message_type || "text";
  const content = String(message.message_content || "").trim();
  if (type === "text" || type === "link") return content;
  if (type === "image") return "📷 Photo";
  if (type === "video") return "🎬 Video";
  if (type === "gif") return "GIF";
  if (type === "doc") return content.split("/").pop() || "Document";
  if (type === "voice" || type === "audio") return "🎤 Voice message";
  if (type === "location") return "📍 Location";
  return content || type;
}

export default function SearchMessage() {
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.userData);
  const [searchText, setSearchText] = useState("");
  const viewImageSlice = useAppSelector((state) => state.ViewManager);
  const translate = useTranslateText();
  const { translate: translateTime, hour12 } = useTimeFormatOptions();
  const { data: SearchMessageData, isLoading } = useSearchMessages(searchText);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // @ts-ignore
  const { theme } = useTheme();

  useEffect(() => {
    if (viewImageSlice?.showSearchMessage === true) {
      setTimeout(() => searchInputRef.current?.focus(), 120);
    } else {
      setSearchText("");
    }
  }, [viewImageSlice?.showSearchMessage]);

  const results = useMemo(() => {
    return (SearchMessageData?.data?.Records || []).filter(
      (message) =>
        message.deleted_for_everyone == false &&
        !message.deleted_for?.includes(userData.user_id.toString()),
    );
  }, [SearchMessageData, userData.user_id]);

  function navigateToThatChat(SearchResult: searchMessageRecord) {
    dispatch(
      updateNavigateToSpesificMessage({
        chat_id: SearchResult.chat_id,
        navigate_to_message: true,
        message_id: SearchResult.message_id,
      }),
    );
  }

  function close() {
    dispatch(updateViewState({ showSearchMessage: false }));
    dispatch(updateSearchMessageResult([]));
    setSearchText("");
  }

  const showEmpty =
    searchText.trim().length > 0 && !isLoading && results.length === 0;
  const showIdle = searchText.trim().length === 0 && !isLoading;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-secondary">
      <div className="sticky top-0 z-20 shrink-0 border-b border-black/[0.06] bg-secondary/85 backdrop-blur-xl dark:border-white/[0.08]">
        <div className="flex items-center justify-between px-3 py-3">
          <button
            type="button"
            aria-label="Close"
            className="grid h-9 w-9 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10"
            onClick={close}
          >
            <IoClose className="text-xl" />
          </button>
          <span className="text-[17px] font-semibold tracking-tight text-darkText">
            {translate("Search Messages")}
          </span>
          <div className="h-9 w-9" aria-hidden />
        </div>

        {/* iOS search field */}
        <div className="px-4 pb-3">
          <div className="relative">
            <IoSearchOutline className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-lightText" />
            <input
              ref={searchInputRef}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                if (e.target.value === "") {
                  dispatch(updateSearchMessageResult([]));
                }
              }}
              className="w-full rounded-[10px] border-0 bg-black/[0.06] py-2.5 pl-10 pr-10 text-[15px] text-darkText outline-none placeholder:text-lightText dark:bg-white/[0.1]"
              type="search"
              enterKeyHint="search"
              placeholder={translate("Search Messages")}
            />
            {isLoading ? (
              <ClipLoader
                className="absolute right-3 top-1/2 -translate-y-1/2"
                color={theme == "dark" ? "white" : "black"}
                size={18}
              />
            ) : searchText ? (
              <button
                type="button"
                aria-label="Clear"
                className="absolute right-2.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-content-center rounded-full bg-black/20 text-[11px] text-white dark:bg-white/25"
                onClick={() => {
                  setSearchText("");
                  dispatch(updateSearchMessageResult([]));
                  searchInputRef.current?.focus();
                }}
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 pb-10">
        {showIdle && (
          <div className="px-2 pt-8 text-center text-[14px] text-lightText">
            {translate("Search Messages")}
          </div>
        )}

        {showEmpty && (
          <div className="grid min-h-[50vh] place-content-center gap-4 text-center">
            <SearchNotFoundAnimation />
            <div className="text-[15px] text-lightText">
              {translate("No Such Message Found")}
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
            {results.map((message, index) => {
              const isOwn = message.User.user_id == userData.user_id;
              return (
                <button
                  key={message.message_id}
                  type="button"
                  onClick={() => navigateToThatChat(message)}
                  className={`flex w-full items-start gap-3 px-3 py-3 text-left active:bg-black/[0.04] dark:active:bg-white/[0.06] ${
                    index > 0
                      ? "border-t border-black/[0.06] dark:border-white/[0.08]"
                      : ""
                  }`}
                >
                  <img
                    src={message.User.profile_pic}
                    alt=""
                    className="mt-0.5 h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="truncate text-[15px] font-semibold text-darkText">
                        {isOwn ? (
                          "You"
                        ) : (
                          <ShowSavedName
                            user_id={message.User.user_id.toString()}
                            name={message.User.full_name}
                          />
                        )}
                      </div>
                      <div className="shrink-0 text-[11px] tabular-nums text-lightText">
                        {chatSidebarTime(message.createdAt, {
                          translate: translateTime,
                          hour12,
                        })}
                      </div>
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-[14px] leading-snug text-darkText/70">
                      {previewForMessage(message)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
